/**
 * Bluetooth Manager - Web Bluetooth API Integration
 * Supports universal data formats from coffee roaster controllers
 */

class BluetoothManager {
    constructor() {
        this.device = null;
        this.server = null;
        this.characteristic = null;
        this.isConnected = false;
        this.dataCallback = null;
        this.statusCallback = null;

        // Universal Service UUIDs (common for serial/UART devices)
        this.UART_SERVICE_UUID = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
        this.UART_TX_CHAR_UUID = '6e400003-b5a3-f393-e0a9-e50e24dcca9e'; // Receive data from device
        this.UART_RX_CHAR_UUID = '6e400002-b5a3-f393-e0a9-e50e24dcca9e'; // Send data to device

        // Data parsing patterns
        this.dataPatterns = [
            // Pattern 1: "BT:230.5,ET:195.2,STATUS:READY"
            /BT:(\d+\.?\d*),ET:(\d+\.?\d*)/i,
            // Pattern 2: "230.5,195.2"
            /^(\d+\.?\d*),(\d+\.?\d*)$/,
            // Pattern 3: JSON format
            /"bt":\s*(\d+\.?\d*),\s*"et":\s*(\d+\.?\d*)/i
        ];
    }

    /**
     * Check if Web Bluetooth is supported
     */
    isSupported() {
        if (!navigator.bluetooth) {
            console.error('Web Bluetooth API is not supported in this browser.');
            return false;
        }
        return true;
    }

    /**
     * Request and connect to a Bluetooth device
     */
    async connect() {
        if (!this.isSupported()) {
            throw new Error('Web Bluetooth not supported');
        }

        try {
            this.updateStatus('scanning');

            // Request device with optional services
            this.device = await navigator.bluetooth.requestDevice({
                acceptAllDevices: true,
                optionalServices: [
                    this.UART_SERVICE_UUID,
                    'battery_service',
                    'device_information',
                    '0000180a-0000-1000-8000-00805f9b34fb' // Device Information
                ]
            });

            console.log('Device selected:', this.device.name);
            this.updateStatus('connecting');

            // Listen for disconnection
            this.device.addEventListener('gattserverdisconnected', () => {
                this.handleDisconnection();
            });

            // Connect to GATT Server
            this.server = await this.device.gatt.connect();
            console.log('Connected to GATT Server');

            // Try to discover and connect to UART service
            await this.setupDataChannel();

            this.isConnected = true;
            this.updateStatus('connected');

            return this.device;

        } catch (error) {
            console.error('Connection error:', error);
            this.updateStatus('error');
            throw error;
        }
    }

    /**
     * Setup data channel (UART characteristics)
     */
    async setupDataChannel() {
        try {
            // Try to get UART service
            const service = await this.server.getPrimaryService(this.UART_SERVICE_UUID);
            console.log('UART Service found');

            // Get TX characteristic (receiving data from device)
            this.characteristic = await service.getCharacteristic(this.UART_TX_CHAR_UUID);

            // Start notifications
            await this.characteristic.startNotifications();

            // Listen for data
            this.characteristic.addEventListener('characteristicvaluechanged', (event) => {
                this.handleDataReceived(event.target.value);
            });

            console.log('Data channel established');

        } catch (error) {
            console.warn('UART service not found, trying alternative methods:', error);
            // Fallback: try to discover all services
            await this.discoverServices();
        }
    }

    /**
     * Discover all available services (fallback method)
     */
    async discoverServices() {
        try {
            const services = await this.server.getPrimaryServices();
            console.log('Discovered services:', services.length);

            for (const service of services) {
                console.log('Service UUID:', service.uuid);
                const characteristics = await service.getCharacteristics();

                for (const char of characteristics) {
                    console.log('  Characteristic UUID:', char.uuid);
                    console.log('  Properties:', char.properties);

                    // If characteristic supports notifications, use it
                    if (char.properties.notify) {
                        console.log('  -> Using this characteristic for data');
                        this.characteristic = char;
                        await char.startNotifications();
                        char.addEventListener('characteristicvaluechanged', (event) => {
                            this.handleDataReceived(event.target.value);
                        });
                        return; // Use first notifiable characteristic
                    }
                }
            }

            throw new Error('No suitable characteristic found for data reception');

        } catch (error) {
            console.error('Service discovery error:', error);
            throw error;
        }
    }

    /**
     * Handle incoming data from Bluetooth device
     */
    handleDataReceived(value) {
        // Convert DataView to string
        const decoder = new TextDecoder('utf-8');
        const dataString = decoder.decode(value);

        console.log('Raw data received:', dataString);

        // Parse data using multiple patterns
        const parsedData = this.parseData(dataString);

        if (parsedData && this.dataCallback) {
            this.dataCallback(parsedData);
        }
    }

    /**
     * Parse data using universal patterns
     */
    parseData(dataString) {
        const data = {
            bt: null,
            et: null,
            status: 'UNKNOWN',
            raw: dataString,
            timestamp: Date.now()
        };

        // Try each pattern
        for (const pattern of this.dataPatterns) {
            const match = dataString.match(pattern);
            if (match) {
                data.bt = parseFloat(match[1]);
                data.et = parseFloat(match[2]);

                // Extract status if available
                const statusMatch = dataString.match(/STATUS:(\w+)/i);
                if (statusMatch) {
                    data.status = statusMatch[1];
                }

                console.log('Parsed data:', data);
                return data;
            }
        }

        // If no pattern matched, try to extract any numbers
        const numbers = dataString.match(/\d+\.?\d*/g);
        if (numbers && numbers.length >= 2) {
            data.bt = parseFloat(numbers[0]);
            data.et = parseFloat(numbers[1]);
            console.log('Fallback parsed data:', data);
            return data;
        }

        console.warn('Could not parse data:', dataString);
        return null;
    }

    /**
     * Send command to device (if supported)
     */
    async sendCommand(command) {
        if (!this.isConnected || !this.characteristic) {
            throw new Error('Not connected to device');
        }

        try {
            // Get RX characteristic for sending
            const service = await this.server.getPrimaryService(this.UART_SERVICE_UUID);
            const rxChar = await service.getCharacteristic(this.UART_RX_CHAR_UUID);

            // Encode command
            const encoder = new TextEncoder();
            const data = encoder.encode(command + '\n');

            await rxChar.writeValue(data);
            console.log('Command sent:', command);

        } catch (error) {
            console.error('Send command error:', error);
            throw error;
        }
    }

    /**
     * Disconnect from device
     */
    disconnect() {
        if (this.device && this.device.gatt.connected) {
            this.device.gatt.disconnect();
            console.log('Disconnected from device');
        }
        this.handleDisconnection();
    }

    /**
     * Handle disconnection event
     */
    handleDisconnection() {
        this.isConnected = false;
        this.server = null;
        this.characteristic = null;
        this.updateStatus('disconnected');
        console.log('Device disconnected');
    }

    /**
     * Auto-reconnect
     */
    async reconnect() {
        if (!this.device) {
            throw new Error('No device to reconnect to');
        }

        try {
            this.updateStatus('connecting');
            this.server = await this.device.gatt.connect();
            await this.setupDataChannel();
            this.isConnected = true;
            this.updateStatus('connected');
            console.log('Reconnected successfully');
        } catch (error) {
            console.error('Reconnection failed:', error);
            this.updateStatus('error');
            throw error;
        }
    }

    /**
     * Register data callback
     */
    onData(callback) {
        this.dataCallback = callback;
    }

    /**
     * Register status callback
     */
    onStatusChange(callback) {
        this.statusCallback = callback;
    }

    /**
     * Update connection status
     */
    updateStatus(status) {
        console.log('Status changed:', status);
        if (this.statusCallback) {
            this.statusCallback(status);
        }
    }

    /**
     * Get device info
     */
    getDeviceInfo() {
        if (!this.device) return null;

        return {
            name: this.device.name,
            id: this.device.id,
            connected: this.isConnected
        };
    }
}

export default BluetoothManager;
